/**
 * ProductDetailPanel
 *
 * Slide-over detail panel for product image selection
 * - Hero image with horizontal gallery
 * - Product info (name, SKU, GTIN, description)
 * - Brand info (locked if from product, selector if not)
 *
 * @accessibility WCAG 2.1 AA compliant
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Image as ImageIcon, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getProduct, getProductAssets, getAIReferenceImages } from '@/lib/api/products';
import { cn } from '@/lib/utils';
import type { Product } from '@/types/products';
import { BrandContextSection } from './BrandContextSection';

/**
 * Get badge display info (icon + color) for role
 */
function getBadgeInfo(role: string): { icon: string; color: string } {
  const roleLower = role.toLowerCase();

  switch (roleLower) {
    case 'packshot':
      return { icon: '📦', color: 'bg-orange-600/90' };
    case 'lifestyle':
      return { icon: '🌿', color: 'bg-green-600/90' };
    case 'detail':
      return { icon: '📸', color: 'bg-primary-600/90' };
    case 'brand':
      return { icon: '🏷️', color: 'bg-primary-600/90' };
    default:
      return { icon: '📋', color: 'bg-neutral-600/90' };
  }
}

interface ProductDetailPanelProps {
  product: Product;
  onBack: () => void;
  onImageSelect: (file: File, imageUrl: string) => void;
  selectedBrandId: string | null;
  onBrandChange: (brandId: string | null) => void;
}

export function ProductDetailPanel({
  product,
  onBack,
  onImageSelect,
  selectedBrandId,
  onBrandChange,
}: ProductDetailPanelProps) {
  const { t } = useTranslation();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const hasAutoSelectedRef = useRef(false);

  // Query for full product details (with brand_id)
  const { data: productResponse, isLoading: isLoadingProduct } = useQuery({
    queryKey: ['product', product.id],
    queryFn: () => getProduct(product.id),
    staleTime: 30_000,
  });

  // Query for product assets (Produktbilder)
  const { data: assetsResponse, isLoading: isLoadingAssets } = useQuery({
    queryKey: ['product-assets', product.id],
    queryFn: () => getProductAssets(product.id),
    staleTime: 30_000,
  });

  // Query for AI reference images (KI-Referenzen)
  const { data: aiRefsResponse, isLoading: isLoadingAIRefs } = useQuery({
    queryKey: ['product-ai-refs', product.id],
    queryFn: () => getAIReferenceImages(product.id),
    staleTime: 30_000,
  });

  const isLoading = isLoadingProduct || isLoadingAssets || isLoadingAIRefs;

  // Get full product with brand_id
  const fullProduct = productResponse?.product || product;

  // AI Reference Images (keep ALL, including duplicates)
  // WICHTIG: Nur s3_key_original verwenden, nicht ai_ref_url/thumbnail_url (direkte S3 URLs)
  const aiRefImages = useMemo(() => {
    const images = (aiRefsResponse?.slots || [])
      .filter(slot => !slot.is_empty && slot.asset_id && slot.s3_key_original)
      .map(slot => ({
        slot: slot.slot,
        asset_id: slot.asset_id!,
        role: slot.role || 'GENERAL',
        s3_key_original: slot.s3_key_original!,
        width: slot.width || null,
        height: slot.height || null,
      }));

    // Debug: Log roles to see exact values from backend
    if (images.length > 0) {
      console.log('[ProductDetailPanel] AI Ref Roles:', images.map(img => `"${img.role}"`).join(', '));
    }

    return images;
  }, [aiRefsResponse]);

  // Product Images (deduplicate)
  const productImages = useMemo(() => {
    const assets = assetsResponse?.assets || [];
    const seen = new Set<string>();
    return assets.filter(asset => {
      const key = asset.asset_id || asset.s3_key_original;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [assetsResponse]);

  // Combined for hero image navigation
  const allImages = useMemo(() => [...aiRefImages, ...productImages], [aiRefImages, productImages]);
  const currentImage = allImages[selectedImageIndex];
  const currentAssetId = currentImage?.asset_id || null;

  // Product has locked brand (from full product data)
  const hasLockedBrand = !!fullProduct.brand_id;
  const effectiveBrandId = (hasLockedBrand ? fullProduct.brand_id : selectedBrandId) || null;

  // Auto-set brand when product with brand_id is loaded
  useEffect(() => {
    if (fullProduct.brand_id && fullProduct.brand_id !== selectedBrandId) {
      console.log('[ProductDetailPanel] Auto-setting brand from product:', fullProduct.brand_id);
      onBrandChange(fullProduct.brand_id);
    }
  }, [fullProduct.brand_id, selectedBrandId, onBrandChange]);

  // Reset auto-select flag when product changes
  useEffect(() => {
    hasAutoSelectedRef.current = false;
    setSelectedImageIndex(0);
    setSelectedImageUrl(null);
  }, [product.id]);

  // Auto-select best image on load (based on priority)
  useEffect(() => {
    if (allImages.length > 0 && !isLoading && !hasAutoSelectedRef.current) {
      // Find best image index based on priority
      let bestIndex = 0;

      if (aiRefImages.length > 0) {
        // Priority order for KI-Referenz roles
        const rolePriority = ['packshot', 'lifestyle', 'detail', 'brand'];

        // Search for best KI-Referenz by priority
        for (const targetRole of rolePriority) {
          const index = aiRefImages.findIndex(img =>
            img.role.toLowerCase() === targetRole
          );
          if (index !== -1) {
            console.log(`[ProductDetailPanel] Auto-selected KI-Referenz: ${targetRole} at index ${index}`);
            bestIndex = index;
            break;
          }
        }

        // If no priority role found, use first image
        if (bestIndex === 0 && aiRefImages[0]) {
          console.log('[ProductDetailPanel] Auto-selected first KI-Referenz at index 0');
        }
      } else if (allImages.length > 0) {
        // Fallback: First product image
        console.log('[ProductDetailPanel] Auto-selected first product image at index 0');
      }

      const bestImage = allImages[bestIndex];
      if (bestImage?.asset_id) {
        setSelectedImageIndex(bestIndex);
        setSelectedImageUrl(bestImage.asset_id);
        hasAutoSelectedRef.current = true; // Mark as auto-selected
      }
    }
  }, [allImages, aiRefImages, isLoading]); // Run when images are loaded

  const handleImageClick = async (image: typeof allImages[0], index: number) => {
    try {
      const assetId = image.asset_id;
      if (!assetId) {
        console.error('[ProductDetailPanel] No asset_id found in image');
        return;
      }

      console.log('[ProductDetailPanel] Image clicked, asset_id:', assetId);

      // Update selected state
      setSelectedImageIndex(index);
      setSelectedImageUrl(assetId);

      // Use /asset/{asset_id}/image endpoint (like gallery /thumb/{image_id})
      const imageUrl = `/api/v1/asset/${assetId}/image`;
      console.log('[ProductDetailPanel] Fetching from:', imageUrl);

      // Fetch image and convert to File
      const response = await fetch(imageUrl);
      console.log('[ProductDetailPanel] Response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('[ProductDetailPanel] Blob received:', blob.type, blob.size, 'bytes');

      const filename = `product-${product.id}-${assetId}.jpg`;
      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });

      console.log('[ProductDetailPanel] File created:', filename, 'Size:', file.size, 'Type:', file.type);
      console.log('[ProductDetailPanel] Calling onImageSelect with file');
      onImageSelect(file, imageUrl);
    } catch (error) {
      console.error('[ProductDetailPanel] Error loading image:', error);
      alert(`Fehler beim Laden des Bildes: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
    }
  };

  return (
    <div className="absolute inset-0 bg-white dark:bg-neutral-900 z-50 flex flex-col animate-slide-in-right">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-neutral-200 dark:border-neutral-800 p-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-orange-600 dark:hover:text-orange-400 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('generation.backToList')}
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : allImages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-neutral-500">
            <ImageIcon className="h-12 w-12 mb-2" />
            <p className="text-sm">{t('gallery.noImages')}</p>
          </div>
        ) : (
          <>
            {/* Hero Image (Clickable) */}
            <div className="flex justify-center">
              <button
                onClick={() => currentImage && handleImageClick(currentImage, selectedImageIndex)}
                className={cn(
                  'max-h-[200px] rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800',
                  'transition-all hover:ring-4 hover:ring-orange-500 hover:opacity-100 focus:outline-none focus:ring-4 focus:ring-orange-500',
                  selectedImageUrl === currentAssetId
                    ? 'ring-[3px] ring-orange-600 shadow-lg shadow-orange-500/50 opacity-100'
                    : 'opacity-70'
                )}
              >
                {currentAssetId && (
                  <img
                    src={`/api/v1/asset/${currentAssetId}/image`}
                    alt={`${fullProduct.name} - Image ${selectedImageIndex + 1}`}
                    className="h-auto max-h-[200px] w-auto object-contain"
                  />
                )}
              </button>
            </div>

            {/* KI-Referenzen Section */}
            {aiRefImages.length > 0 && (
              <div className="space-y-2 border-t border-neutral-200 dark:border-neutral-800 pt-4">
                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                  KI-Referenzen ({aiRefImages.length})
                </p>
                <div className="flex gap-3 overflow-x-auto p-1">
                  {aiRefImages.map((img, idx) => {
                    const assetId = img.asset_id;
                    if (!assetId) return null;

                    return (
                      <button
                        key={`ai-ref-${img.slot}-${idx}`}
                        onClick={() => handleImageClick(img, idx)}
                        className={cn(
                          'relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800',
                          'transition-all hover:ring-4 hover:ring-orange-500 hover:opacity-100 focus:outline-none focus:ring-4 focus:ring-orange-500',
                          selectedImageUrl === assetId
                            ? 'ring-[3px] ring-orange-600 shadow-lg shadow-orange-500/50 opacity-100'
                            : 'opacity-70'
                        )}
                      >
                        <img
                          src={`/api/v1/asset/${assetId}/image`}
                          alt={`KI-Referenz ${img.role}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {/* Role Badge - Top Right Pill */}
                        {(() => {
                          const badgeInfo = getBadgeInfo(img.role);
                          return (
                            <div className={cn(
                              "absolute top-1 right-1 px-1.5 py-0.5 rounded-full shadow-sm flex items-center gap-0.5",
                              badgeInfo.color
                            )}>
                              <span className="text-2xs">{badgeInfo.icon}</span>
                              <span className="text-3xs font-medium text-white whitespace-nowrap capitalize">
                                {img.role.toLowerCase()}
                              </span>
                            </div>
                          );
                        })()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Produktbilder Section */}
            {productImages.length > 0 && (
              <div className="space-y-2 border-t border-neutral-200 dark:border-neutral-800 pt-4">
                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                  Produktbilder ({productImages.length})
                </p>
                <div className="flex gap-3 overflow-x-auto p-1">
                  {productImages.map((asset, idx) => {
                    const assetId = asset.asset_id;
                    if (!assetId) return null;

                    const globalIndex = aiRefImages.length + idx;

                    return (
                      <button
                        key={asset.asset_id}
                        onClick={() => handleImageClick(asset, globalIndex)}
                        className={cn(
                          'flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800',
                          'transition-all hover:ring-4 hover:ring-orange-500 hover:opacity-100 focus:outline-none focus:ring-4 focus:ring-orange-500',
                          selectedImageUrl === assetId
                            ? 'ring-[3px] ring-orange-600 shadow-lg shadow-orange-500/50 opacity-100'
                            : 'opacity-70'
                        )}
                      >
                        <img
                          src={`/api/v1/asset/${assetId}/image`}
                          alt={`Produktbild ${idx + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Product Info */}
            <div className="space-y-3 border-t border-neutral-200 dark:border-neutral-800 pt-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  {fullProduct.name}
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                {fullProduct.sku && (
                  <div>
                    <span className="text-neutral-500 dark:text-neutral-400">SKU:</span>
                    <div className="font-medium text-neutral-900 dark:text-neutral-100">{fullProduct.sku}</div>
                  </div>
                )}
                {fullProduct.gtin && (
                  <div>
                    <span className="text-neutral-500 dark:text-neutral-400">GTIN:</span>
                    <div className="font-medium text-neutral-900 dark:text-neutral-100">{fullProduct.gtin}</div>
                  </div>
                )}
              </div>

              {/* Description (Collapsible) */}
              {fullProduct.description && (
                <div className="space-y-2">
                  <button
                    onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                    className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors w-full"
                  >
                    {isDescriptionExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    {t('common.description')}
                  </button>

                  {isDescriptionExpanded && (
                    <div className="text-sm text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 rounded-lg p-3 leading-relaxed">
                      {fullProduct.description}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Brand Context Section */}
            <BrandContextSection
              selectedBrandId={effectiveBrandId}
              onBrandChange={onBrandChange}
              isLocked={hasLockedBrand}
              lockedMessage={t('generation.brandLockedLong')}
            />
          </>
        )}
      </div>
    </div>
  );
}
